import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "templateSelect",
    "previewEyebrow",
    "previewSubject",
    "previewPreheader",
    "previewHeadline",
    "previewIntro",
    "previewBody",
    "previewCta",
    "previewFooter",
    "previewUsername",
    "userCheckbox",
    "selectedCount",
    "submitButton",
    "selectAll",
    "userDialog",
    "userDialogTitle",
    "userDialogEmail",
    "userDialogJoinedAt",
    "userDialogLastSeenAt",
    "userDialogAppsCount",
    "userDialogTestsCount"
  ]

  static values = {
    templates: Object,
    previewUser: Object,
    confirmMessage: String,
    templateRequiredMessage: String,
    usersRequiredMessage: String
  }

  connect() {
    this.syncSelection()
    this.renderPreview()
  }

  templateChanged() {
    this.renderPreview()
    this.syncSelection()
  }

  toggleAll() {
    const checked = this.selectAllTarget.checked
    this.userCheckboxTargets.forEach((checkbox) => {
      checkbox.checked = checked
    })
    this.syncSelection()
  }

  selectionChanged() {
    this.syncSelection()
    this.renderPreview()
  }

  openUserModal(event) {
    const trigger = event.currentTarget

    this.userDialogTitleTarget.textContent = trigger.dataset.userName || ""
    this.userDialogEmailTarget.textContent = trigger.dataset.userEmail || ""
    this.userDialogJoinedAtTarget.textContent = trigger.dataset.userJoinedAt || ""
    this.userDialogLastSeenAtTarget.textContent = trigger.dataset.userLastSeenAt || ""
    this.userDialogAppsCountTarget.textContent = trigger.dataset.userAppsCount || "0"
    this.userDialogTestsCountTarget.textContent = trigger.dataset.userTestsCount || "0"

    this.userDialogTarget.showModal()
  }

  closeUserModal() {
    this.userDialogTarget.close()
  }

  confirmSubmit(event) {
    const selectedTemplate = this.selectedTemplate()
    const selectedUsers = this.selectedUsers()

    if (!selectedTemplate) {
      event.preventDefault()
      window.alert(this.templateRequiredMessageValue)
      return
    }

    if (selectedUsers.length === 0) {
      event.preventDefault()
      window.alert(this.usersRequiredMessageValue)
      return
    }

    const templateLabel = selectedTemplate.label || this.templateSelectTarget.selectedOptions[0]?.textContent || ""
    const message = this.confirmMessageValue
      .replace("%{count}", selectedUsers.length)
      .replace("%{template}", templateLabel)

    if (!window.confirm(message)) {
      event.preventDefault()
      return
    }

    this.submitButtonTarget.disabled = true
    this.submitButtonTarget.classList.add("opacity-60")
  }

  renderPreview() {
    const template = this.selectedTemplate()
    if (!template) return

    const previewUser = this.activePreviewUser()
    const username = previewUser.username || "username"

    this.previewEyebrowTarget.textContent = this.interpolate(template.eyebrow, previewUser)
    this.previewSubjectTarget.textContent = this.interpolate(template.subject, previewUser)
    this.previewPreheaderTarget.textContent = this.interpolate(template.preheader, previewUser)
    this.previewHeadlineTarget.textContent = this.interpolate(template.headline, previewUser)
    this.previewIntroTarget.textContent = this.interpolate(template.intro, previewUser)
    this.previewBodyTarget.innerHTML = this.interpolate(template.body, previewUser).replace(/\n/g, "<br>")
    this.previewCtaTarget.textContent = this.interpolate(template.cta_label, previewUser)
    this.previewCtaTarget.href = template.cta_url
    this.previewFooterTarget.textContent = this.interpolate(template.footer, previewUser)
    this.previewUsernameTarget.textContent = username
  }

  syncSelection() {
    const selectedUsers = this.selectedUsers()
    const count = selectedUsers.length

    this.selectedCountTargets.forEach((target) => {
      target.textContent = count
    })

    if (this.hasSelectAllTarget) {
      this.selectAllTarget.checked = count > 0 && count === this.userCheckboxTargets.length
      this.selectAllTarget.indeterminate = count > 0 && count < this.userCheckboxTargets.length
    }

    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = count === 0
      this.submitButtonTarget.classList.toggle("opacity-60", count === 0)
    }
  }

  selectedUsers() {
    return this.userCheckboxTargets.filter((checkbox) => checkbox.checked)
  }

  selectedTemplate() {
    const option = this.templateSelectTarget.selectedOptions[0]
    const optionPayload = option?.dataset?.templatePayload

    if (optionPayload) {
      try {
        return JSON.parse(optionPayload)
      } catch (_error) {
      }
    }

    return this.templatesValue?.[this.templateSelectTarget.value]
  }

  activePreviewUser() {
    const selectedUser = this.selectedUsers()[0]
    if (!selectedUser) return this.previewUserValue || {}

    return {
      username: selectedUser.dataset.username,
      email: selectedUser.dataset.email
    }
  }

  interpolate(content, user) {
    return String(content || "").replaceAll("{username}", user.username || "username")
  }
}
